import { FilterBar, FilterBarField } from "design-system/molecules/FilterBar"
import { isFilled } from "lopermut"
import { useGoalsQuery } from "modules/campaigns/hooks/sections/goals/useGoalsQuery"
import { useCustomersQuery } from "modules/customers/hooks/useCustomersQuery"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useTranslation } from "react-i18next"
import { Route } from "routes/_base/_auth/campaigns/$id/activity/route"

type Search = ReturnType<typeof Route.useSearch>
type ResponsesFilterBy = NonNullable<Search["filterBy"]>

export const CampaignResponsesFilter = () => {
  const { t } = useTranslation()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { id } = Route.useParams()
  const labels = useFilterBarLabels()

  const { data: goals = [] } = useGoalsQuery({
    query: {
      filterBy: { campaignId: id },
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
  })

  const { data: customers = [] } = useCustomersQuery({
    query: {
      page: 1,
      total: 100,
      sortBy: { field: "name", order: "asc" },
      filterBy: { campaignId: id, hasResponses: true, isFake: false },
    },
  })

  function onChangeFilter(changes: Partial<ResponsesFilterBy>) {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        filterBy: { ...previous.filterBy, ...changes },
      }),
    })
  }

  function onClearFilters() {
    navigate({
      search: (previous) => ({ ...previous, page: 1, filterBy: undefined, query: undefined }),
    })
  }

  const fields: FilterBarField[] = [
    {
      key: "goalId",
      type: "select",
      label: t("campaigns.responses.filters.goalId"),
      value: search.filterBy?.goalId,
      onChange: (goalId) => onChangeFilter({ goalId }),
      options: goals.map((goal) => ({ value: goal.id, label: goal.displayName })),
    },
    {
      key: "customerId",
      type: "select",
      label: t("campaigns.responses.filters.customer"),
      value: search.filterBy?.customerId,
      onChange: (customerId) => onChangeFilter({ customerId }),
      options: customers.map((customer) => ({
        value: customer.id,
        label: isFilled(customer.name) ? customer.name : t("customers.profile.noName"),
      })),
    },
  ]

  return (
    <FilterBar
      className="w-full"
      fields={fields}
      labels={labels}
      onClearFilters={onClearFilters}
      searchQuery={search.query}
      onSearchQueryChange={(query) =>
        navigate({ search: (previous) => ({ ...previous, query: query || undefined, page: 1 }) })
      }
      searchPlaceholder={t("campaigns.responses.filters.search")}
    />
  )
}
